import { Axis, type Island, Islands, type KitchenSize, Panel } from '@rta/rta-blueprint-library';

import { DesignerOptions } from '@r-core';
import { getInlineIslandChain, layoutInlineRun } from '../helpers/get-inline-island-chain';
import { RenderCountertop } from '../render/render-countertop';
import { RenderPanel } from '../render/render-panel';
import { renderTracking } from '@r-services/render-tracking';

import { CreateCornerPattern, CreateCountertopPattern } from '../symbols';

export type ShowInlineIslandParams = {
	island: Island;
	islands: Islands;
	rotation: Axis;
	panel: Panel;
	kitchenSize: KitchenSize;
	options: DesignerOptions;
};

export const showInlineIslands = (params: ShowInlineIslandParams): React.ReactElement => {
	const { island: openedIsland, panel, options } = params;
	if (openedIsland === null) return <></>;

	// the opened island plus every island joined to it inline (via left/right side panels), left-to-right
	const chain = getInlineIslandChain(openedIsland);
	if (chain.length === 0) return <></>;

	const panelAngle = panel === null
		? null
		: panel.getOriginAngle();

	// Width must match what RenderPanel/RenderCountertop actually draws: the panel at panelAngle (each island's own front/left/right/back, relative to ITS OWN front), not a transform of the island's absolute world axis.
	const alongRowWidthOf = (island: Island): number => (panelAngle === null
		? island.islandSize.width
		: (island.panels.findPanelByAngle(panelAngle)?.size.width ?? 0));
	// The face being looked at, in world terms: the island's own facing plus this panel's angle from its
	// front. layoutInlineRun projects each island's real coordinates onto that direction, so front/back
	// and every world facing fall out of one formula instead of a mirror rule (#646).
	const { positionOf } = layoutInlineRun({
		chain,
		widthOf: alongRowWidthOf,
		viewAngle: openedIsland.axis.y + (panelAngle ?? 0)
	});

	renderTracking.incrementRender('DisplayInline');
	return <>{
		[...chain]
			.sort(panel === null || panel.isFrontPanel || panel.isBackPanel
				? Islands.sortIslandsByHeight
				: panel.isLeftPanel
					? Islands.sortIslandsByLeftView
					: Islands.sortIslandsByRightView)
			.map((island) => {
				const tPanel = island.panels.findPanelByAngle(panelAngle);
				const islandId = island.id;

				// panel elevation needs a face at this angle; a cross-orientation leg may have none, so skip it rather than render an undefined panel
				if (panel !== null && tPanel === null) return null;

				const xPos = positionOf(island);

				const node = <g key={islandId}
					transform={`
						translate(
							${xPos},
							0)
						`}>
					<CreateCountertopPattern {...{ countertop: island.getCountertop(), options }} />
					<CreateCornerPattern {...{ material: island.refMap?.kitchen?.material ?? null, options }} />

					{/* A value of -1 represents counter */}
					{panel === null
						? <RenderCountertop {...{ islandId }}/>
						: <RenderPanel {...{ islandId, panelId: tPanel?.id ?? null }}/>
					}
				</g>;

				return node;
			})
	}</>;
};
